<script setup>
import {
  cloudPbxPlans,
  metamaPlans,
  roundPlans,
  faxPlans,
  crmPlans,
  smsPlans,
} from "~/data/plans";
import { metamaPackage } from "~/data/compareData";
import { OffersCatalogSchema } from "~/schemas/OfferCatalog";
import { cloudPbxPrices } from "~/data/prices/cloud-pbx";
import { crmPrices } from "~/data/prices/crm";

const { oldFormatPrice } = useInputHelpers();

const isModalShow = ref(false);
const rondPlansUserCount = ref(10);
const cloudPbxPlansUserCount = ref(10);
const crmPlansUserCount = ref(10);

const channel9169 = {
  title: "قیمت سرشماره و هزینه هر کانال (بوق آزاد) پیش‌شماره 9",
  items: ["خرید شماره از 300,000 تومان", "خرید هر کانال ماهانه 15,000 تومان"],
  desc: [
    "قیمت‌ها بر اساس مصوبات کمیسیون تنظیم مقررات و تعرفه اپراتورها در هر سال به روز رسانی می‌شوند.",
    "قیمت‌ها بدون احتساب %10 مالیات می‌باشند.",
  ],
};

const mappedRondPlans = computed(() => {
  if (!roundPlans) return [];
  return roundPlans.map((bundleItem, index) => ({
        ...bundleItem,
        price: cloudPbxPrices
          .filter(pricing => pricing.prefix === '8')[index]
          .amounts.map(amount => {
            if (!Array.isArray(amount.value)) return bundleItem.price;
            const matchedTier = amount.value.find(item => {
                const min = item.min ?? 1;
                const max = item.max ?? Infinity;
                return rondPlansUserCount.value >= min && rondPlansUserCount.value <= max;
            });

            if (!matchedTier) return bundleItem.price;
            return oldFormatPrice(matchedTier.price);
          })
    }));
});

const mappedCloudPbxPlans = computed(() => {
  if (!cloudPbxPlans) return [];
  return cloudPbxPlans.map((bundleItem, index) => ({
        ...bundleItem,
        price: cloudPbxPrices
          .filter(pricing => pricing.prefix === '9')[index]
          .amounts.map(amount => {
            if (!Array.isArray(amount.value)) return bundleItem.price;
            const matchedTier = amount.value.find(item => {
                const min = item.min ?? 1;
                const max = item.max ?? Infinity;
                return cloudPbxPlansUserCount.value >= min && cloudPbxPlansUserCount.value <= max;
            });

            if (!matchedTier) return bundleItem.price;
            return oldFormatPrice(matchedTier.price);
          })
    }));
});

const mappedCrmPlans = computed(() => {
  if (!crmPlans) return [];
  return crmPlans.map((bundleItem, index) => ({
        ...bundleItem,
        price: crmPrices[index]
          .amounts.map(amount => {
            if (!Array.isArray(amount.value)) return bundleItem.price;
            const matchedTier = amount.value.find(item => {
                const min = item.min ?? 1;
                const max = item.max ?? Infinity;
                return crmPlansUserCount.value >= min && crmPlansUserCount.value <= max;
            });

            if (!matchedTier) return bundleItem.price;
            return oldFormatPrice(matchedTier.price);
          })
    }));
});

const channel8280 = {
  title: "قیمت سرشماره و هزینه هر کانال (بوق آزاد) پیش‌شماره 8",
  items: ["خرید شماره از 500,000 تومان", "خرید هر کانال ماهانه 96,000 تومان"],
  desc: [
    "نواتل تنها ارائه کننده خطوط ابری مخابراتی برای استان تهران با پیش شماره 8280 می‌باشد.",
    "امکان استفاده و فعال‌سازی این خطوط بر روی سایر سرویس‌های ابری نواتل نیز وجود دارد.",
    "با فعال سازی این خطوط بروی اپلیکیشن اختصاصی نواتل از امکانات متنوع آن بهره‌مند شوید.",
  ],
};

useSeoMeta({
  ogUrl: "https://navatel.ir/prices/",
  ogTitle: "تعرفه خدمات نواتل",
  ogDescription: "تعرفه‌ها و جزئیات بسته‌های نواتل شامل مرکز تلفن ابری، CRM ابری و  سایر خدمات ارتباطی را به‌راحتی مشاهده و انتخاب کنید.",
})

useHead({
  meta: [
    {
      name: 'description',
      content: 'تعرفه‌ها و جزئیات بسته‌های نواتل شامل مرکز تلفن ابری، CRM ابری و  سایر خدمات ارتباطی را به‌راحتی مشاهده و انتخاب کنید.'
    }
  ],
  link: [
    {
      rel: "canonical",
      href: "https://navatel.ir/prices/",
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(OffersCatalogSchema)
    },
  ],
});
</script>

<template>
  <div class="gridClass my-10">
    <div class="bg-blue-700 w-full text-white rounded-2xl">
      <h1 class="py-10 text-center">
        تعرفه خدمات نواتل
      </h1>
    </div>
  </div>
  <SectionPlan
    :items="mappedRondPlans"
    title="بسته‌های مرکز‌ تلفن ابری نواتل با پیش‌شماره 8"
    sub-title="(پیش شماره‌های 8280 و 8164 و 8721)"
    title-tag="h2"
    hide-compare
    isShowRangeInput
    v-model="rondPlansUserCount"
    class="mt-16"
    freeSetupChip
  />
  <SectionPlan
    :items="mappedCloudPbxPlans"
    title="بسته‌های مرکز تلفن ابری نواتل با پیش‌شماره 9"
    sub-title="(پیش شماره‌ 9169)"
    class="mt-16"
    isShowRangeInput
    hide-compare
    v-model="cloudPbxPlansUserCount"
    freeSetupChip
  />
  <SectionTablePrice :info="channel8280" class="mt-16" />
  <SectionTablePrice :info="channel9169" class="mt-16" />
  <SectionTableCompare
    title="تعرفه‌های برقراری تماس با پیش شماره‌ی 8"
    tci
    class="mt-16"
  />
  <SectionTableCompare
    title="مقایسه تعرفه تماس نواتل با تماس‌های مخابراتی (بر حسب تومان بر دقیقه)"
    tci
    nava
    class="mt-16"
  />
  <PricesInternetPhone class="mb-5" title="تعرفه تلفن اینترنتی نواتل برای پیش شماره 8" prefix="8" />
  <PricesInternetPhone class="mb-5" title="تعرفه تلفن اینترنتی نواتل برای پیش شماره 9" prefix="9" />
  <SectionPlan
    :items="mappedCrmPlans"
    isShowRangeInput
    v-model="crmPlansUserCount"
    title="بسته های مدیریت ارتباط با مشتری (سی آر ام ابری) نواتل"
    hideCompare
  />
  <ProductSmsPlans
    :items="smsPlans"
    title="جزئیات تعرفه خرید پنل پیامکی نواتل بر اساس امکانات متنوع"
  />
  <PricesWebchat title="تعرفه سرویس چت آنلاین نواتل" class="mt-16" />
  <SectionPlan
    :items="faxPlans"
    hideCompare
    title="تعرفه بسته های فکس ابری نواتل"
    desc="با فکس ابری نواتل، بدون نیاز به هیچ‌گونه تجهیزات فیزیکی و تنها با اتصال به اینترنت، فکس‌های خود را از طریق موبایل یا لپ‌تاپ ارسال و دریافت کنید."
    class="mt-16"
  />
  <PricesCrm
    title="تعرفه اتصال مرکز تلفن نواتل به سی آر ام"
    class="mt-16"
  />
  <section class="gridClass mb-8 mt-16" id="metama_price">
    <div
      class="text-base lg:text-2xl font-bold text-center mb-10 lg:mb-14"
    >
      بسته های مرکز تماس ابری متاما
    </div>
    <div class="mb-10 tablet:flex justify-around gap-4">
      <div
        class="w-full tablet:w-[30%] lg:w-[25%] mt-12 lg:mt-0 p-4 custom-shadow bg-white relative rounded-xl"
        v-for="item in metamaPlans"
        :key="'f' + item.id"
      >
        <ProductPlan :item="item" />
      </div>
    </div>
    <div
      class="py-6 px-2 mt-8 mb-12 bg-gray-200 flex flex-col lg:flex-row justify-center items-center"
    >
      <span class="me-6 font-bold text-black text-sm"
        >در مورد بسته ها نیاز به اطلاعات بیشتر دارید؟</span
      >
      <BaseButton
        @click="isModalShow = true"
        isLean
        size="2xl"
        text="مقایسه بسته ها"
        color="primary"
      />
    </div>
    <SectionModalCompare
      :isShow="isModalShow"
      @close="isModalShow = false"
      :data="metamaPackage"
    />
  </section>
  <SectionTableCompare
    title="تعرفه‌های برقراری تماس با متاما"
    tci
    class="mt-16"
  />
</template>
